/**
 * The worlds and what is in them that a lesson can point at (ADR-032):
 * pure data, imported by framing (to face a far feature) and by worlds.ts
 * (to judge a lesson against it) without either importing the other.
 */
/** The code worlds' slugs: what a lesson could name before packages (part 3b, V7). */
export const CODE_WORLD_SLUGS = [
    'volcanic-island',
    'science-lab',
    'forest',
    'meadow',
    'polder',
    'beach',
    'sea-floor',
    'town-square',
    'modern-street',
    'museum-hall',
    'villa',
];
export const WORLDS = [
    {
        slug: 'volcanic-island',
        name: 'Vulkaaneiland',
        part: 1,
        features: {
            // 260 m off, 60° right of ahead; 110 m high (the code island's, now the volcanic island
            // package's: volcanic-island-package.test.ts holds it).
            vulkaan: {
                name: 'De vulkaan',
                states: ['rustig', 'rokend', 'uitbarsting'],
                initial: 'rokend',
                round: { centre: [225.17, 50, -130], radius: 120, height: 60 },
            },
        },
    },
    {
        slug: 'science-lab',
        name: 'Practicumlokaal',
        part: 1,
        features: {
            // The lit burner on the left bench (the code lab's, now the science lab package's: science-lab-package.test.ts holds it).
            bunsenbrander: {
                name: 'De bunsenbrander',
                states: ['aan', 'uit'],
                initial: 'aan',
                round: { centre: [-9.05, 1.1, -1.9], radius: 0.15, height: 0.2 },
            },
        },
    },
    {
        slug: 'forest',
        name: 'Loofbos',
        part: 2,
        features: {
            // The old beech 35° to the left, 36 m off down a lane, trunk and crown, 24 m tall
            // (the code forest's, now the forest package's: forest-package.test.ts holds it).
            seizoen: {
                name: 'Het seizoen',
                states: ['zomer', 'herfst'],
                initial: 'zomer',
                round: { centre: [-20.77, 11, -29.47], radius: 9, height: 14 },
            },
        },
    },
    {
        slug: 'meadow',
        name: 'Bloemenweide',
        part: 2,
        features: {
            // The patch just past the apron, 25° to the right (the code meadow's, now the meadow package's:
            // meadow-package.test.ts holds it).
            vlinders: {
                name: 'De vlinders',
                states: ['weg', 'fladderen'],
                initial: 'fladderen',
                round: { centre: [4.86, 0.9, -10.42], radius: 2.2, height: 1 },
            },
        },
    },
    {
        slug: 'polder',
        name: 'Polder',
        part: 2,
        features: {
            // The open water beyond the dike round its gauge, ahead to the left (the code polder's, now
            // the polder package's: polder-package.test.ts holds it).
            water: {
                name: 'Het water',
                states: ['laag', 'hoog'],
                initial: 'laag',
                round: { centre: [-17.84, -1.17, -16.06], radius: 7, height: 1.4 },
            },
            // The mill on the polder, 40° to the right, 62 m off.
            molen: {
                name: 'De molen',
                states: ['stil', 'draaien'],
                initial: 'draaien',
                round: { centre: [37.54, 9.8, -44.93], radius: 11, height: 15 },
            },
        },
    },
    {
        slug: 'beach',
        name: 'Strand',
        part: 2,
        features: {
            // The flat wet sand between low and high water, ahead (the code beach's, now the beach
            // package's: beach-package.test.ts holds it).
            getij: {
                name: 'Het getij',
                states: ['eb', 'vloed'],
                initial: 'eb',
                round: { centre: [0, -1.02, -23.64], radius: 9, height: 0.8 },
            },
        },
    },
    {
        slug: 'sea-floor',
        name: 'Zeebodem',
        part: 2,
        features: {
            // The school off to the left, 13 m away (the code sea floor's, now the sea floor package's:
            // sea-floor-package.test.ts holds it).
            vissen: {
                name: 'De vissen',
                states: ['weg', 'zwemmen'],
                initial: 'zwemmen',
                round: { centre: [-8.36, 2.5, -9.96], radius: 4.2, height: 1.5 },
            },
        },
    },
    {
        slug: 'town-square',
        name: 'Marktplein',
        part: 2,
        features: {
            // The row of stalls ahead to the left, past the apron (the code town square's, now the town
            // square package's: town-square-package.test.ts holds it).
            markt: {
                name: 'De markt',
                states: ['leeg', 'markt'],
                initial: 'leeg',
                round: { centre: [-7.9, 1.3, -13], radius: 6, height: 1.3 },
            },
        },
    },
    {
        slug: 'modern-street',
        name: 'Stadsstraat',
        part: 2,
        features: {
            // The street ahead, its lanes and cycle paths (the code street's, now the modern street
            // package's: modern-street-package.test.ts holds it).
            verkeer: {
                name: 'Het verkeer',
                states: ['rustig', 'druk'],
                initial: 'rustig',
                round: { centre: [0, 0.8, -18.5], radius: 7, height: 0.85 },
            },
        },
    },
    {
        slug: 'museum-hall',
        name: 'Museumzaal',
        part: 2,
        features: {
            // The tall window at the hall's head, ahead (the code museum's, now the museum hall package's: museum-hall-package.test.ts holds it).
            licht: {
                name: 'Het licht',
                states: ['dag', 'avond'],
                initial: 'dag',
                round: { centre: [0, 4.8, -13.55], radius: 2.8, height: 2.45 },
            },
        },
    },
    {
        // A modern villa on a dune above the beach, empty for a class to furnish (ADR-037): its rooms
        // are in villa.ts; it has no features and no stage.
        slug: 'villa',
        name: 'Villa aan zee',
        part: 3,
        staged: false,
        features: {},
    },
];
/** How a lesson names a feature: `wereld:vulkaan`. */
export const FEATURE_PREFIX = 'wereld:';
/** The feature a reference names, or undefined when it is not one. */
export function featureOf(reference) {
    if (typeof reference !== 'string' || !reference.startsWith(FEATURE_PREFIX))
        return undefined;
    return reference.slice(FEATURE_PREFIX.length);
}
/** The result key of a hotspot on a feature (ADR-087): `wereld:bloedstroom`, paged `wereld:bloedstroom#2`. */
export function featureKey(feature) {
    return `${FEATURE_PREFIX}${feature}`;
}
/**
 * What the code worlds are called in English and say about themselves: the generator's line (English,
 * what it is and what it is for) and the teacher's (Dutch, formal), moved here from the scene list so
 * a package of the same slug can take their place (world-catalogue.ts).
 */
export const CODE_WORLD_TEXTS = {
    'volcanic-island': {
        nameEn: 'Volcanic island',
        description: 'Volcanic island: dark ash underfoot, a smoking volcano far to the right. For volcanoes, rocks, islands, the sea.',
        teacher: {
            title: 'Vulkaaneiland',
            text: 'Een eiland in zee met een rokende vulkaan in de verte. Voor vulkanen, gesteenten, eilanden en de zee.',
        },
    },
    'science-lab': {
        nameEn: 'Science lab',
        description: 'Bright science lab: benches, glassware, microscopes, Bunsen burners. For chemistry, physics, biology, cells, lab safety.',
        teacher: {
            title: 'Practicumlokaal',
            text: 'Een licht practicumlokaal met werkbanken, glaswerk, twee microscopen en bunsenbranders. Voor scheikunde, natuurkunde, biologie, cellen en veilig werken.',
        },
    },
    forest: {
        nameEn: 'Forest',
        description: 'Forest clearing among tall beeches: moss, ferns, sunbeams, drifting leaves. For trees, plants, animals, seasons.',
        teacher: {
            title: 'Loofbos',
            text: 'Een open plek tussen hoge beuken, met mos, varens en zonlicht. Voor bomen, planten, dieren en de seizoenen.',
        },
    },
    meadow: {
        nameEn: 'Flower meadow',
        description: 'Flowering meadow in spring: poppies, daisies, old oaks, a hedgerow. For plants, insects, pollination, food webs.',
        teacher: {
            title: 'Bloemenweide',
            text: 'Een bloeiende weide in de lente, met klaprozen, madeliefjes en oude eiken. Voor planten, insecten, bestuiving en voedselwebben.',
        },
    },
    polder: {
        nameEn: 'Polder',
        description: 'Dutch polder seen from a dike: water higher than the land, ditches, a windmill. For water, the Netherlands, energy.',
        teacher: {
            title: 'Polder',
            text: 'Vanaf de dijk: water hoger dan het land, sloten, een molen. Voor water, Nederland en energie.',
        },
    },
    beach: {
        nameEn: 'Beach',
        description: 'North Sea beach: dry sand, surf, dunes with marram grass, old posts, gulls. For the coast, tides, sand, the sea.',
        teacher: {
            title: 'Strand',
            text: 'Een Noordzeestrand met branding, duinen met helmgras en meeuwen. Voor de kust, eb en vloed, zand en de zee.',
        },
    },
    'sea-floor': {
        nameEn: 'Sea floor',
        description: 'Sea floor 14 m down: sand, rocks, kelp, caustics, bubbles, light from above. For sea life, the ocean, water and light.',
        teacher: {
            title: 'Zeebodem',
            text: 'Veertien meter onder water: zand, rotsen, kelp en licht van boven. Voor het leven in zee, de oceaan, water en licht.',
        },
    },
    'town-square': {
        nameEn: 'Market square',
        description: 'Historic Dutch market square: gabled brick houses, a town hall with a clock, a church tower. For history, towns, trade.',
        teacher: {
            title: 'Marktplein',
            text: 'Een oud Nederlands marktplein met trapgevels, een stadhuis met een klok en een kerktoren. Voor geschiedenis, steden en handel.',
        },
    },
    'modern-street': {
        nameEn: 'City street',
        description: 'Modern Dutch city square by a street: glass and concrete blocks, cycle paths, bikes. For traffic, cities, safety, design.',
        teacher: {
            title: 'Stadsstraat',
            text: 'Een modern plein aan een straat in de stad: glas, beton, fietspaden en fietsen. Voor verkeer, de stad, veiligheid en ontwerp.',
        },
    },
    'museum-hall': {
        nameEn: 'Museum hall',
        description: 'Museum hall: stone floor, columns, a skylight, paintings, exhibits on plinths. For art, history, collections, heritage.',
        teacher: {
            title: 'Museumzaal',
            text: 'Een museumzaal met zuilen, een lichtkoepel, schilderijen en voorwerpen op sokkels. Voor kunst, geschiedenis, collecties en erfgoed.',
        },
    },
    villa: {
        nameEn: 'Villa by the sea',
        description: "A modern villa on a dune above the sea, empty. For furnishing with the pupils' own furniture; not for generated lessons.",
        teacher: {
            title: 'De villa',
            text: "Leerlingen richten samen een villa op een duin in met meubels die ze zelf ontwierpen (STL-bestanden). Dit is een vaste les, 'De villa inrichten': u hoeft er geen lesidee voor te sturen. U uploadt de meubels onder Werkstukken.",
        },
    },
};
//# sourceMappingURL=world-data.js.map